import { useState } from 'react';
import { useCategories } from '../hooks/useFoundItems';
import type { FoundItem } from '../types';

type ItemFormData = {
	title: string;
	description: string;
	categoryId: string;
	photoUrl: string;
	foundLocationText: string;
	foundLatitude: string;
	foundLongitude: string;
};

interface ItemFormProps {
	initialValues?: Partial<ItemFormData>;
	onSubmit: (data: ItemFormData) => void;
	isPending?: boolean;
	submitLabel?: string;
}

export function ItemForm({
	initialValues,
	onSubmit,
	isPending,
	submitLabel = 'Salvar',
}: ItemFormProps) {
	const { data: categories } = useCategories();

	const [form, setForm] = useState<ItemFormData>({
		title: initialValues?.title ?? '',
		description: initialValues?.description ?? '',
		categoryId: initialValues?.categoryId ?? '',
		photoUrl: initialValues?.photoUrl ?? '',
		foundLocationText: initialValues?.foundLocationText ?? '',
		foundLatitude: initialValues?.foundLatitude ?? '',
		foundLongitude: initialValues?.foundLongitude ?? '',
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	function validate(): boolean {
		const errs: Record<string, string> = {};
		if (!form.title.trim()) errs.title = 'Título é obrigatório';
		if (!form.categoryId) errs.categoryId = 'Selecione uma categoria';
		if (!form.photoUrl.trim()) errs.photoUrl = 'Informe a URL da foto';
		if (!form.foundLocationText.trim()) errs.foundLocationText = 'Informe o local';
		setErrors(errs);
		return Object.keys(errs).length === 0;
	}

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (validate()) onSubmit(form);
	}

	function set(field: keyof ItemFormData, value: string) {
		setForm((prev) => ({ ...prev, [field]: value }));
		if (errors[field])
			setErrors((prev) => {
				const next = { ...prev };
				delete next[field];
				return next;
			});
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			<div>
				<label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
					Título *
				</label>
				<input
					id="title"
					type="text"
					value={form.title}
					onChange={(e) => set('title', e.target.value)}
					className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.title ? 'border-red-400' : 'border-gray-300'}`}
				/>
				{errors.title && <p className="text-sm text-red-500 mt-1">{errors.title}</p>}
			</div>

			<div>
				<label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
					Descrição
				</label>
				<textarea
					id="description"
					value={form.description}
					onChange={(e) => set('description', e.target.value)}
					rows={3}
					className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
				/>
			</div>

			<div>
				<label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-1">
					Categoria *
				</label>
				<select
					id="categoryId"
					value={form.categoryId}
					onChange={(e) => set('categoryId', e.target.value)}
					className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.categoryId ? 'border-red-400' : 'border-gray-300'}`}
				>
					<option value="">Selecione uma categoria</option>
					{categories?.map((c) => (
						<option key={c.id} value={c.id}>
							{c.name}
						</option>
					))}
				</select>
				{errors.categoryId && <p className="text-sm text-red-500 mt-1">{errors.categoryId}</p>}
			</div>

			<div>
				<label htmlFor="photoUrl" className="block text-sm font-medium text-gray-700 mb-1">
					URL da Foto *
				</label>
				<input
					id="photoUrl"
					type="url"
					value={form.photoUrl}
					onChange={(e) => set('photoUrl', e.target.value)}
					placeholder="https://..."
					className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.photoUrl ? 'border-red-400' : 'border-gray-300'}`}
				/>
				{errors.photoUrl && <p className="text-sm text-red-500 mt-1">{errors.photoUrl}</p>}
				{form.photoUrl && (
					<div className="mt-2 relative aspect-video w-full max-w-xs bg-gray-100 rounded-lg overflow-hidden">
						<img
							src={form.photoUrl}
							alt="Preview"
							className="w-full h-full object-cover"
							onError={(e) => {
								(e.target as HTMLImageElement).style.display = 'none';
							}}
						/>
					</div>
				)}
			</div>

			<div>
				<label htmlFor="foundLocationText" className="block text-sm font-medium text-gray-700 mb-1">
					Local encontrado *
				</label>
				<input
					id="foundLocationText"
					type="text"
					value={form.foundLocationText}
					onChange={(e) => set('foundLocationText', e.target.value)}
					className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.foundLocationText ? 'border-red-400' : 'border-gray-300'}`}
				/>
				{errors.foundLocationText && (
					<p className="text-sm text-red-500 mt-1">{errors.foundLocationText}</p>
				)}
			</div>

			<div className="grid grid-cols-2 gap-4">
				<div>
					<label htmlFor="foundLatitude" className="block text-sm font-medium text-gray-700 mb-1">
						Latitude
					</label>
					<input
						id="foundLatitude"
						type="number"
						step="any"
						value={form.foundLatitude}
						onChange={(e) => set('foundLatitude', e.target.value)}
						className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
					/>
				</div>
				<div>
					<label htmlFor="foundLongitude" className="block text-sm font-medium text-gray-700 mb-1">
						Longitude
					</label>
					<input
						id="foundLongitude"
						type="number"
						step="any"
						value={form.foundLongitude}
						onChange={(e) => set('foundLongitude', e.target.value)}
						className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
					/>
				</div>
			</div>

			<div className="flex gap-3">
				<button
					type="submit"
					disabled={isPending}
					className="px-6 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
				>
					{isPending ? 'Salvando...' : submitLabel}
				</button>
			</div>
		</form>
	);
}
